import { useState, useEffect, useCallback } from "react";
import { Activity, ChevronLeft, ChevronRight, Filter } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { adminApi } from "@/lib/api";
import { toast } from "sonner";

const ACTION_LABELS = {
  login: "Login",
  data_upload: "Data Upload",
  user_created: "User Created",
  user_deleted: "User Deleted",
  invoice_generated: "Invoice Generated",
  invoice_published: "Invoice Published",
  invoice_paid: "Invoice Paid",
  invoice_cancelled: "Invoice Cancelled",
  settings_updated: "Settings Updated",
  data_sync: "Data Sync",
  company_created: "Company Created",
  company_updated: "Company Updated",
  product_sandbox_toggled: "Product Sandbox Toggled",
};

const ACTION_COLORS = {
  login: "bg-blue-50 text-blue-600",
  data_upload: "bg-emerald-50 text-emerald-600",
  user_created: "bg-purple-50 text-purple-600",
  user_deleted: "bg-red-50 text-red-600",
  invoice_generated: "bg-amber-50 text-amber-600",
  invoice_published: "bg-cyan-50 text-cyan-600",
  invoice_paid: "bg-emerald-50 text-emerald-600",
  invoice_cancelled: "bg-red-50 text-red-500",
  settings_updated: "bg-slate-100 text-slate-600",
  data_sync: "bg-indigo-50 text-indigo-600",
};

const PAGE_SIZE = 25;

export default function ActivityLog({ api = adminApi }) {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState("all");
  const [actions, setActions] = useState([]);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, skip: page * PAGE_SIZE };
      if (actionFilter && actionFilter !== "all") params.action = actionFilter;
      const res = await api.getAuditLogs(params);
      setLogs(res.data.logs);
      setTotal(res.data.total);
    } catch { toast.error("Failed to load activity logs"); }
    setLoading(false);
  }, [page, actionFilter]);

  const fetchActions = useCallback(async () => {
    try {
      const res = await api.getAuditActions();
      setActions(res.data);
    } catch {}
  }, []);

  useEffect(() => { fetchActions(); }, [fetchActions]);
  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const formatDetails = (action, details) => {
    if (!details || Object.keys(details).length === 0) return "—";
    if (action === "login") return `Username: ${details.username || "—"}`;
    if (action === "data_upload") return `${details.filename || "file"} → ${details.records_inserted || 0} records`;
    if (action === "user_created") return `${details.name} (${details.role})`;
    if (action === "user_deleted") return `ID: ${details.partner_id}`;
    if (action === "data_sync") return `${details.rows_inserted || 0} inserted, ${details.rows_updated || 0} updated`;
    return Object.entries(details).map(([k, v]) => `${k}: ${v === null ? "Unavailable" : v}`).join(", ");
  };

  return (
    <div className="p-6 md:p-8" data-testid="activity-log-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Activity Log
          </h1>
          <p className="text-sm text-slate-500 mt-1">Track all system actions and changes</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={actionFilter} onValueChange={(v) => { setActionFilter(v); setPage(0); }}>
            <SelectTrigger className="w-[180px] h-9 border-slate-200 text-sm" data-testid="action-filter">
              <Filter size={14} className="mr-1.5 text-slate-400" />
              <SelectValue placeholder="All Actions" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Actions</SelectItem>
              {actions.map((a) => (
                <SelectItem key={a} value={a}>{ACTION_LABELS[a] || a}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="audit-table">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Time</TableHead>
              <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Action</TableHead>
              <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">User</TableHead>
              <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Role</TableHead>
              <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Details</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5} className="text-center py-12 text-slate-400">Loading...</TableCell></TableRow>
            ) : logs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-12 text-slate-400">
                  <div className="flex flex-col items-center gap-2">
                    <Activity size={28} className="text-slate-300" />
                    <p>No activity logs yet</p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              logs.map((log) => (
                <TableRow key={log.log_id} className="border-b border-slate-100 hover:bg-slate-50/50">
                  <TableCell className="py-3 px-4 text-xs text-slate-500 whitespace-nowrap">
                    {new Date(log.timestamp).toLocaleString()}
                  </TableCell>
                  <TableCell className="py-3 px-4">
                    <span className={`inline-flex items-center text-xs font-medium px-2.5 py-1 rounded-full ${ACTION_COLORS[log.action] || "bg-slate-100 text-slate-600"}`}>
                      {ACTION_LABELS[log.action] || log.action}
                    </span>
                  </TableCell>
                  <TableCell className="py-3 px-4 text-sm text-slate-700 font-mono">{log.user_id}</TableCell>
                  <TableCell className="py-3 px-4">
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${log.role === "admin" ? "bg-blue-50 text-blue-600" : log.role === "user" ? "bg-purple-50 text-purple-600" : "bg-slate-100 text-slate-600"}`}>
                      {log.role}
                    </span>
                  </TableCell>
                  <TableCell className="py-3 px-4 text-sm text-slate-600 max-w-xs truncate">
                    {formatDetails(log.action, log.details)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4" data-testid="audit-pagination">
          <span className="text-xs text-slate-500">
            Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total}
          </span>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(page - 1)} className="h-8 w-8 p-0" data-testid="audit-prev">
              <ChevronLeft size={16} />
            </Button>
            <span className="text-xs text-slate-600 px-2">{page + 1} / {totalPages}</span>
            <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(page + 1)} className="h-8 w-8 p-0" data-testid="audit-next">
              <ChevronRight size={16} />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
